type CategoryValue = string | number | boolean;

const categoryKey = (value: unknown) => `${typeof value}:${String(value ?? "")}`;

const naturalCompare = (left: unknown, right: unknown) =>
  String(left ?? "").localeCompare(String(right ?? ""), "th", {
    numeric: true,
    sensitivity: "base",
  });

const uniqueValues = (values: unknown[] | null | undefined): CategoryValue[] => {
  const seen = new Set<string>();
  return (values ?? []).reduce<CategoryValue[]>((result, value) => {
    const normalized = (value ?? "") as CategoryValue;
    const key = categoryKey(normalized);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(normalized);
    }
    return result;
  }, []);
};

/**
 * รวม domain ของ category จาก backend กับค่าที่พบในกราฟปัจจุบัน
 * domain จาก backend ทำให้สีคงที่ข้ามช่วงเวลา ส่วนค่าปัจจุบันช่วยไม่ให้
 * category ที่เพิ่งเพิ่มหายไปก่อน pipeline รอบถัดไปจะเผยแพร่ข้อมูล
 */
export const getCategoryDomain = (
  spec: any,
  field: string | undefined,
  observedValues: unknown[] | null | undefined,
  fallbackValues: unknown[] | null | undefined = [],
): CategoryValue[] => {
  const configured = field && Array.isArray(spec?._category_orders?.[field])
    ? spec._category_orders[field]
    : fallbackValues;
  const domain = uniqueValues(configured);
  const known = new Set(domain.map(categoryKey));
  const remaining = uniqueValues(observedValues)
    .filter((value) => !known.has(categoryKey(value)))
    .sort(naturalCompare);
  return [...domain, ...remaining];
};

export const getPresentCategoryValues = (
  observedValues: unknown[] | null | undefined,
  domain: CategoryValue[],
): CategoryValue[] => {
  const position = new Map(domain.map((value, index) => [categoryKey(value), index]));
  return uniqueValues(observedValues).sort((left, right) => {
    const leftPosition = position.get(categoryKey(left)) ?? Number.MAX_SAFE_INTEGER;
    const rightPosition = position.get(categoryKey(right)) ?? Number.MAX_SAFE_INTEGER;
    return leftPosition === rightPosition
      ? naturalCompare(left, right)
      : leftPosition - rightPosition;
  });
};

export const sortRowsByCategoryOrder = <T extends Record<string, any>>(
  rows: T[],
  field: string | undefined,
  domain: CategoryValue[],
): T[] => {
  if (!field) return rows;
  const position = new Map(domain.map((value, index) => [categoryKey(value), index]));
  return [...rows].sort((left, right) => {
    const leftValue = left[field] ?? "";
    const rightValue = right[field] ?? "";
    const leftPosition = position.get(categoryKey(leftValue)) ?? Number.MAX_SAFE_INTEGER;
    const rightPosition = position.get(categoryKey(rightValue)) ?? Number.MAX_SAFE_INTEGER;
    return leftPosition === rightPosition
      ? naturalCompare(leftValue, rightValue)
      : leftPosition - rightPosition;
  });
};
